import type { DAGData, DAGEdge, DAGNode } from "../../api/artifacts";

const KINDS = ["geometry", "surface_mesh", "volume_mesh", "run", "plot", "solver_config", "report"] as const;
const HUMAN_KINDS = ["slack_message", "meeting_note", "decision"] as const;

/** Deterministic, branchy scale fixture: 10k nodes yield almost exactly 20k edges. */
export function generateSyntheticDag(nodeCount: number): DAGData {
  const count = Math.max(1, Math.floor(nodeCount));
  const start = Date.parse("2026-01-01T00:00:00Z");
  const nodes: DAGNode[] = Array.from({ length: count }, (_, index) => {
    const human = index > 0 && index % 11 === 0;
    const operation = !human && index % 7 === 0;
    const kind = human
      ? HUMAN_KINDS[index % HUMAN_KINDS.length]!
      : operation
        ? "operation_run"
        : KINDS[index % KINDS.length]!;
    const invalidated = index > 0 && index % 127 === 0;
    const timestamp = new Date(start + index * 37_000 + (index % 23) * 1_300).toISOString();
    return {
      id: `synthetic-${String(index).padStart(6, "0")}`,
      path: `${kind}.${String(index).padStart(6, "0")}`,
      kind,
      type: kind,
      name: human ? `Design record ${index}` : `${kind.replace(/_/g, " ")} ${index}`,
      created_at: timestamp,
      state: invalidated ? "invalidated" : "active",
      digest: `sha256:${String((index * 2654435761) >>> 0).padStart(10, "0")}`,
      anchor: human && index % 121 === 0,
      metadata: human ? { channel: `#project-${index % 6}`, source_created_at: timestamp } : {},
    };
  });

  const edges: DAGEdge[] = [];
  for (let index = 1; index < count; index += 1) {
    const primaryParent = Math.max(0, index - 1 - (index % 5));
    edges.push({
      id: `synthetic-edge-${String(edges.length).padStart(7, "0")}`,
      parent_id: nodes[primaryParent]!.id,
      child_id: nodes[index]!.id,
      relationship: index % 11 === 0 ? "mention" : "derived_from",
      created_at: String(nodes[index]!.created_at),
    });
    if (index > 3) {
      const span = 2 + (index * 17) % Math.min(index, 97);
      const secondaryParent = Math.max(0, index - span);
      edges.push({
        id: `synthetic-edge-${String(edges.length).padStart(7, "0")}`,
        parent_id: nodes[secondaryParent]!.id,
        child_id: nodes[index]!.id,
        relationship: index % 127 === 0 ? "invalidated_by" : "input_to",
        created_at: String(nodes[index]!.created_at),
      });
    }
  }
  return { nodes, edges };
}
